import React, { useCallback, useEffect, useState } from 'react';
import { Handshake, Loader2, RefreshCcw, Trash2 } from 'lucide-react';
import api from '@/lib/axios';

const ESTADO_STYLES = {
  proposto: 'bg-slate-100 text-slate-700',
  aceite: 'bg-cyan-50 text-cyan-700',
  publicado: 'bg-amber-50 text-amber-800',
  validado: 'bg-emerald-50 text-emerald-700',
  recusado: 'bg-rose-50 text-rose-700',
  expirado: 'bg-slate-100 text-slate-500',
};
const PROXIMOS = {
  proposto: ['aceite', 'recusado'],
  aceite: ['publicado', 'recusado'],
  publicado: ['validado', 'recusado'],
  validado: ['expirado'],
};
const vazio = {
  nome: '', email: '', instagram: '', tiktok: '', outra_rede: '', beneficio: 'publicacao',
  duracao_dias: 30, valor_comercial: 0, contrapartida: '', url_publicacao: '', observacoes: '',
};

export default function MarketplaceInfluencersPanel() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ contadores: {}, valor_comercial_atribuido: 0 });
  const [beneficios, setBeneficios] = useState([]);
  const [filtro, setFiltro] = useState({ estado: '', q: '' });
  const [form, setForm] = useState(vazio);
  const [editing, setEditing] = useState(null);
  const [nota, setNota] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (filtro.estado) params.set('estado', filtro.estado);
      if (filtro.q) params.set('q', filtro.q);
      const res = await api.get(`/admin/marketplace/divulgacao?${params.toString()}`);
      setItems(res.data.items || []);
      setMeta({ contadores: res.data.contadores || {}, valor_comercial_atribuido: res.data.valor_comercial_atribuido || 0 });
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar parcerias.'); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/admin/marketplace/divulgacao/beneficios')
      .then((r) => setBeneficios(r.data.beneficios || [])).catch(() => {});
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const guardar = async (event) => {
    event.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      const body = { ...form, duracao_dias: Number(form.duracao_dias) || 30, valor_comercial: Number(form.valor_comercial) || 0 };
      if (editing) await api.put(`/admin/marketplace/divulgacao/${editing}`, body);
      else await api.post('/admin/marketplace/divulgacao', body);
      setMsg(editing ? 'Parceria atualizada.' : 'Parceria criada.');
      setForm(vazio); setEditing(null);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Não foi possível guardar.'); }
    finally { setBusy(false); }
  };

  const mudarEstado = async (id, estado, urlAtual) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.post(`/admin/marketplace/divulgacao/${id}/estado`, { estado, nota, url_publicacao: urlAtual || '' });
      setMsg(`Estado atualizado para ${estado}.`); setNota('');
      await load();
    } catch (e) { setErr(e.response?.data?.detail || 'Ação não aplicada.'); }
    finally { setBusy(false); }
  };

  const apagar = async (id, estado) => {
    if (!window.confirm('Apagar esta parceria de divulgação?')) return;
    setBusy(true); setErr('');
    try {
      await api.delete(`/admin/marketplace/divulgacao/${id}?motivo=${encodeURIComponent(nota)}`);
      setMsg('Parceria apagada.'); await load();
    } catch (e) { setErr(e.response?.data?.detail || `Não foi possível apagar (${estado}).`); }
    finally { setBusy(false); }
  };

  const editar = (p) => {
    setEditing(p.id);
    setForm({
      nome: p.nome, email: p.email, instagram: p.instagram || '', tiktok: p.tiktok || '',
      outra_rede: p.outra_rede || '', beneficio: p.beneficio, duracao_dias: p.duracao_dias,
      valor_comercial: p.valor_comercial, contrapartida: p.contrapartida || '',
      url_publicacao: p.url_publicacao || '', observacoes: p.observacoes || '',
    });
  };

  const input = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs';

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-influencers-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Handshake className="h-4 w-4 text-emerald-600" /> Parcerias de divulgação
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="admin-influencers-reload">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Benefícios oferecidos a criadores de conteúdo (Instagram, TikTok e outras redes). A publicação é verificada manualmente — sem integração de APIs.
      </p>

      <form onSubmit={guardar} className="mt-3 grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3" data-testid="admin-influencers-form">
        <input className={input} placeholder="Nome" value={form.nome} onChange={(e) => set({ nome: e.target.value })} required data-testid="influencer-name" />
        <input className={input} type="email" placeholder="Email" value={form.email} onChange={(e) => set({ email: e.target.value })} required data-testid="influencer-email" />
        <select className={input} value={form.beneficio} onChange={(e) => set({ beneficio: e.target.value })} data-testid="influencer-benefit">
          {beneficios.map((b) => <option key={b.valor} value={b.valor}>{b.label}</option>)}
        </select>
        <input className={input} placeholder="Instagram" value={form.instagram} onChange={(e) => set({ instagram: e.target.value })} data-testid="influencer-instagram" />
        <input className={input} placeholder="TikTok" value={form.tiktok} onChange={(e) => set({ tiktok: e.target.value })} data-testid="influencer-tiktok" />
        <input className={input} placeholder="Outra rede" value={form.outra_rede} onChange={(e) => set({ outra_rede: e.target.value })} data-testid="influencer-other" />
        <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Duração (dias)
          <input className={input} type="number" min="1" max="365" value={form.duracao_dias} onChange={(e) => set({ duracao_dias: e.target.value })} data-testid="influencer-days" />
        </label>
        <label className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Valor comercial equivalente (€)
          <input className={input} type="number" min="0" step="0.01" value={form.valor_comercial} onChange={(e) => set({ valor_comercial: e.target.value })} data-testid="influencer-value" />
        </label>
        <input className={input} placeholder="URL da publicação (https://)" value={form.url_publicacao} onChange={(e) => set({ url_publicacao: e.target.value })} data-testid="influencer-url" />
        <input className={`${input} sm:col-span-2`} placeholder="Contrapartida acordada" value={form.contrapartida} onChange={(e) => set({ contrapartida: e.target.value })} data-testid="influencer-deal" />
        <input className={input} placeholder="Observações" value={form.observacoes} onChange={(e) => set({ observacoes: e.target.value })} data-testid="influencer-notes" />
        <div className="flex gap-2 sm:col-span-3">
          <button type="submit" disabled={busy} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60" data-testid="admin-influencers-save">
            {editing ? 'Guardar alterações' : 'Criar parceria'}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(vazio); }} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="admin-influencers-cancel">Cancelar</button>
          )}
        </div>
      </form>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={filtro.estado} onChange={(e) => setFiltro((f) => ({ ...f, estado: e.target.value }))}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" data-testid="admin-influencers-filter-state">
          <option value="">Todos os estados</option>
          {Object.keys(ESTADO_STYLES).map((s) => <option key={s} value={s}>{s} ({meta.contadores?.[s] ?? 0})</option>)}
        </select>
        <input value={filtro.q} onChange={(e) => setFiltro((f) => ({ ...f, q: e.target.value }))}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs" placeholder="Nome, email ou rede" data-testid="admin-influencers-search" />
        <input value={nota} onChange={(e) => setNota(e.target.value)}
          className="flex-1 rounded-lg border border-amber-300 bg-amber-50/60 px-2 py-1.5 text-xs" placeholder="Nota / motivo (obrigatório para recusar)" data-testid="admin-influencers-note" />
        <span className="text-xs font-semibold text-slate-500" data-testid="admin-influencers-value">
          Valor atribuído: {(meta.valor_comercial_atribuido || 0).toFixed(2)} €
        </span>
      </div>

      {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="admin-influencers-error">{err}</p>}
      {msg && <p className="mt-2 text-xs font-semibold text-emerald-700" data-testid="admin-influencers-msg">{msg}</p>}

      <div className="mt-3 overflow-x-auto">
        {loading ? (
          <p className="flex items-center gap-2 py-6 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
        ) : items.length === 0 ? (
          <p className="py-6 text-xs text-slate-500" data-testid="admin-influencers-empty">Sem parcerias registadas.</p>
        ) : (
          <table className="w-full min-w-[860px] text-left text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr><th className="py-2">Criador</th><th>Redes</th><th>Benefício</th><th>Valor</th><th>Estado</th><th>Publicação</th><th /></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`influencer-row-${p.id}`}>
                  <td className="py-2">
                    <p className="font-semibold text-slate-800">{p.nome}</p>
                    <p className="text-[10px] text-slate-400">{p.email}</p>
                  </td>
                  <td className="text-[10px] text-slate-600">
                    {p.instagram && <p>IG: {p.instagram}</p>}
                    {p.tiktok && <p>TikTok: {p.tiktok}</p>}
                    {p.outra_rede && <p>{p.outra_rede}</p>}
                  </td>
                  <td>{p.beneficio_label} · {p.duracao_dias} dias</td>
                  <td>{Number(p.valor_comercial || 0).toFixed(2)} €</td>
                  <td>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ESTADO_STYLES[p.estado] || ''}`} data-testid={`influencer-state-${p.id}`}>{p.estado}</span>
                    {p.expira_em && <p className="mt-0.5 text-[10px] text-slate-400">até {p.expira_em.slice(0, 10)}</p>}
                  </td>
                  <td className="max-w-[160px] truncate text-[10px] text-slate-500">
                    {p.url_publicacao ? <a href={p.url_publicacao} target="_blank" rel="noreferrer nofollow" className="text-emerald-700 underline">ver publicação</a> : '—'}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(PROXIMOS[p.estado] || []).map((next) => (
                        <button key={next} type="button" disabled={busy} onClick={() => mudarEstado(p.id, next, form.url_publicacao || p.url_publicacao)}
                          className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`influencer-to-${next}-${p.id}`}>{next}</button>
                      ))}
                      <button type="button" onClick={() => editar(p)} className="rounded border border-slate-300 px-1.5 py-0.5 font-semibold" data-testid={`influencer-edit-${p.id}`}>Editar</button>
                      <button type="button" disabled={busy} onClick={() => apagar(p.id, p.estado)} className="rounded border border-rose-300 px-1.5 py-0.5 font-semibold text-rose-700" data-testid={`influencer-delete-${p.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
